import { z } from 'zod';
import { BillType } from '../enums/billType';
import { ZohoCustomerType } from '../enums/zohoCustomerType';
import { PaymentStatus } from '../enums/paymentStatus';

// Supported ISO country codes for custom pricing
export const SupportedCountryCode = z.enum([
  'IN', // India
]);
export type SupportedCountryCode = z.infer<typeof SupportedCountryCode>;

// Category pricing schema matching PartialCategoryPricing type
// Using string literals to avoid circular dependency with Category enum
export const categoryPricingSchema = z.object({
  MARKETING: z.number().positive().optional(),
  UTILITY: z.number().positive().optional(),
  AUTHENTICATION: z.number().positive().optional(),
});

// RCS flat pricing by country ISO code -> flat INR per message (NO category).
// India-only today (SupportedCountryCode); stays country-keyed so other countries
// can be added later without a data migration. Example: { "IN": 0.1 }
export const rcsCustomPricingSchema = z.record(
  SupportedCountryCode,
  z.number().positive(),
);
export type RcsCustomPricingConfig = z.infer<typeof rcsCustomPricingSchema>;

// Custom (business-level) pricing config, stored on Business.customPricing:
// - country ISO code (e.g. "IN") -> WhatsApp category pricing (existing)
// - "rcs" -> flat RCS pricing by country (new; no category)
// Modelled as an explicit object (not z.record) so the reserved `rcs` key lives
// alongside the country keys in the SAME JSON column (no new column). Backward
// compatible: existing `{ IN: {...} }` data validates unchanged.
// .strict() so unknown/mis-cased keys ERROR (matching the old z.record(enum)
// behavior) instead of being silently stripped — a stripped-to-{} config would
// overwrite the business's real pricing on save.
export const CustomPricingConfig = z
  .object({
    IN: categoryPricingSchema.optional(),
    rcs: rcsCustomPricingSchema.optional(),
  })
  .strict();
export type CustomPricingConfig = z.infer<typeof CustomPricingConfig>;

// ---- Slab (tiered) pricing ----
// A single retroactive volume tier. `minVolume` is the inclusive lower bound of
// the org-wide category volume (across all businesses + all countries) at which
// `rate` (INR per message) applies to ALL India messages of that category.
// `minVolume: 0` is the base tier used below the first discount threshold.
export const slabTierSchema = z.object({
  minVolume: z.number().int().nonnegative(),
  rate: z.number().positive(),
});
export type SlabTier = z.infer<typeof slabTierSchema>;

// An ordered list of tiers for one category. Must be non-empty, include a base
// tier (minVolume 0, applied below the first discount threshold), and have
// strictly ascending, unique minVolume values. Enforced on the backend here, so
// an invalid config can never be persisted (the UI mirrors these rules).
export const slabTierArraySchema = z
  .array(slabTierSchema)
  .min(1, 'At least one tier is required')
  .superRefine((tiers, ctx) => {
    if (!tiers.some(tier => tier.minVolume === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Slab tiers must include a base tier with minVolume 0',
      });
    }
    for (let i = 1; i < tiers.length; i++) {
      if (tiers[i].minVolume <= tiers[i - 1].minVolume) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'Slab tiers must have strictly ascending, unique minVolume values',
        });
        break;
      }
    }
  });

// Per-category slab tiers (only tiered categories are present).
export const categorySlabsSchema = z.object({
  MARKETING: slabTierArraySchema.optional(),
  UTILITY: slabTierArraySchema.optional(),
  AUTHENTICATION: slabTierArraySchema.optional(),
});

// RCS slab tiers by country ISO code -> tiers (NO category). Mirrors the WhatsApp
// org slab ladder but categoryless (a flat org RCS price is a single base tier).
// India-only today; country-keyed for future extension. Example:
// { "IN": [ {minVolume:0,rate:0.1}, {minVolume:1000000,rate:0.08} ] }
export const rcsSlabPricingSchema = z.record(
  SupportedCountryCode,
  slabTierArraySchema,
);
export type RcsSlabPricingConfig = z.infer<typeof rcsSlabPricingSchema>;

// Slab (org-level) pricing config, stored on Org.slabPricing:
// - country ISO code (e.g. "IN") -> per-category tiers (existing). Example:
//   { "IN": { "UTILITY": [ {minVolume:0,rate:0.115}, {minVolume:2500000,rate:0.10} ] } }
// - "rcs" -> RCS tiers by country (new; no category)
// Explicit object (not z.record) so the reserved `rcs` key lives alongside the
// country keys in the SAME JSON column. Backward compatible with `{ IN: {...} }`.
// .strict() so unknown/mis-cased keys ERROR (matching the old z.record(enum)
// behavior) instead of being silently stripped — a stripped-to-{} slabPricing
// would overwrite the org's real tier ladder on save.
export const slabPricingConfigSchema = z
  .object({
    IN: categorySlabsSchema.optional(),
    rcs: rcsSlabPricingSchema.optional(),
  })
  .strict();
export type SlabPricingConfig = z.infer<typeof slabPricingConfigSchema>;

// Bill type schema using the actual BillType enum for type compatibility
export const billTypeSchema = z.nativeEnum(BillType);
export type BillTypeValue = z.infer<typeof billTypeSchema>;

export const adminOrgSchema = z.object({
  id: z.number(),
  name: z.string().nullish(),
  hostname: z.string().nullish(),
  defaultBillType: billTypeSchema.nullish(),
  defaultZohoId: z.string().nullish(),
  slabPricing: slabPricingConfigSchema.nullish(),
});

export const adminBusinessSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullish(),
  businessAccountId: z.string().nullish(),
  bizWhatsappNumber: z.string().nullish(),
  phoneNumberId: z.string().nullish(),
  countryCode: z.number().nullish(),
  sector: z.string().nullish(),
  orgId: z.number().nullish(),
  org: adminOrgSchema.nullish(),
  signUpType: z.string().nullish(),
  isEmbeddedSignUp: z.boolean().nullish(),
  createdAt: z.union([z.string(), z.date()]).nullish(),
  updatedAt: z.union([z.string(), z.date()]).nullish(),
  hasFbAccessToken: z.boolean().nullish(),
  hasApiKey: z.boolean().nullish(),
  customPricing: CustomPricingConfig.nullish(),
  canViewCost: z.boolean().nullish(),
  zohoCustomerId: z.string().nullish(),
  odooCustomerId: z.string().nullish(),
  booksUsername: z.string().nullish(),
  hasKarixData: z.boolean().optional(),
});

// Admin updates to a Business (extensible — add new optional fields here over time)
export const updateBusinessAdminRequestSchema = z
  .object({
    canViewCost: z.boolean().optional(),
  })
  .refine(d => Object.values(d).some(v => v !== undefined), {
    message: 'At least one field must be provided',
  });

export const updateBusinessAdminResponseSchema = z.object({
  id: z.number(),
  canViewCost: z.boolean().nullish(),
});

export const adminBusinessesResponseSchema = z.array(adminBusinessSchema);

// ============================================
// Org Management Schemas
// ============================================

// Org with aggregated stats for admin table
export const adminOrgWithStatsSchema = z.object({
  id: z.number(),
  name: z.string(),
  hostname: z.string(),
  defaultBillType: billTypeSchema,
  defaultZohoId: z.string().nullable().optional(),
  slabPricing: slabPricingConfigSchema.nullish(),
  businessCount: z.number(),
  uniqueZohoIdCount: z.number(),
  partnerId: z.number().nullable().optional(),
  partnerName: z.string().nullable().optional(),
  createdAt: z.union([z.string(), z.date()]),
  updatedAt: z.union([z.string(), z.date()]),
});
export type AdminOrgWithStats = z.infer<typeof adminOrgWithStatsSchema>;

export const adminOrgsResponseSchema = z.array(adminOrgWithStatsSchema);

// Update org request - partial update, only send fields that changed.
// Org pricing is entirely slab-based (a flat price is a single base tier).
export const updateOrgRequestSchema = z.object({
  defaultBillType: billTypeSchema.optional(),
  defaultZohoId: z.string().nullable().optional(),
  slabPricing: slabPricingConfigSchema.nullable().optional(),
});
export type UpdateOrgRequest = z.infer<typeof updateOrgRequestSchema>;

export const updateOrgResponseSchema = z.object({
  id: z.number(),
  name: z.string(),
  defaultBillType: billTypeSchema,
  defaultZohoId: z.string().nullable().optional(),
  slabPricing: slabPricingConfigSchema.nullish(),
  updatedAt: z.union([z.string(), z.date()]),
});
export type UpdateOrgResponse = z.infer<typeof updateOrgResponseSchema>;

// Generate bills by org ID (uses billRangeSchema - defined below)
// Note: generateBillsByOrgRequestSchema is an alias of billRangeSchema, defined after billRangeSchema

export const generateBillsByOrgResponseSchema = z.object({
  orgId: z.number(),
  orgName: z.string(),
  totalBusinesses: z.number(),
  successCount: z.number(),
  failedCount: z.number(),
  results: z.array(
    z.object({
      businessId: z.number(),
      businessName: z.string(),
      success: z.boolean(),
      error: z.string().optional(),
    }),
  ),
});
export type GenerateBillsByOrgResponse = z.infer<
  typeof generateBillsByOrgResponseSchema
>;

// Legacy schema (kept for backwards compatibility)
export const messagingVolumeRowSchema = z.object({
  businessId: z.number(),
  businessName: z.string().nullish(),
  category: z.string().nullish(),
  categoryCount: z.number(),
});

// New country-wise volume schema
export const messagingVolumeByCountryRowSchema = z.object({
  countryCode: z.number(),
  isoCode: z.string(),
  category: z.string().nullish(),
  volume: z.number(),
  /*
   * IST day (YYYY-MM-DD). Present only on daily-grouped fetches (cost
   * analytics' day-wise breakdown); bills aggregate the whole period and
   * never set it.
   */
  date: z.string().optional(),
});

// Volume with cost calculation
export const messagingVolumeWithCostSchema =
  messagingVolumeByCountryRowSchema.extend({
    unitPrice: z.number(),
    cost: z.number(),
  });

// RCS volume with cost — RCS is billed flat per message (NO category). For v1 a
// bill typically has a single RCS row (all messages at the flat rate); kept as an
// array (mirroring messagingVolume) so per-country rows can be added later.
export const rcsVolumeWithCostSchema = z.object({
  countryCode: z.number(),
  isoCode: z.string(),
  volume: z.number(),
  unitPrice: z.number(),
  cost: z.number(),
});
export type RcsVolumeWithCost = z.infer<typeof rcsVolumeWithCostSchema>;

export const openAiUsageRowSchema = z.object({
  model: z.string().nullish(),
  promptTokens: z.number(),
  completionTokens: z.number(),
  cachedTokens: z.number(),
  totalTokens: z.number(),
  totalCostUsd: z.number(),
});

export const billResponseSchema = z.object({
  businessId: z.number(),
  businessName: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  /* When these figures were computed — ISO, set wherever a bill is BUILT.
     `startDate`/`endDate` say which period the bill is for; this says how far
     into that period the numbers actually reach, which is the only thing
     separating a settled bill from a mid-month estimate stored under the same
     boundaries (see isFinalBill).

     It lives in the payload, not beside the row's updated_at, so that every
     rewrite that derives new figures from old ones — slab re-pricing returns
     `{ ...billData, … }` — carries it forward untouched, with nobody having to
     decide. Optional for bills saved before it existed; those fall back to the
     row's updatedAt, which for a row nothing has re-saved is the same thing. */
  computedAt: z.string().optional(),
  messagingVolume: z.array(messagingVolumeWithCostSchema),
  // RCS messaging volume + cost. Optional so bills saved before RCS billing
  // (billData JSON without these keys) still validate on read; freshly generated
  // bills always set them, and every consumer guards with `?? []` / `?? 0`.
  rcsVolume: z.array(rcsVolumeWithCostSchema).optional(),
  totalRcsCost: z.number().optional(),
  openAiUsage: z.array(openAiUsageRowSchema),
  totalMessagingCost: z.number(),
  totalOpenAiCostUsd: z.number(),
  usdToInrRate: z.number(),
  totalOpenAiCostInr: z.number(),
  totalCost: z.number(),
});

// Base date range schema - used by multiple bill generation endpoints
export const billRangeSchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
});

// Alias for generate bills by org (same schema as billRangeSchema)
export const generateBillsByOrgRequestSchema = billRangeSchema;
export type GenerateBillsByOrgRequest = z.infer<
  typeof generateBillsByOrgRequestSchema
>;

// Generate single bill response (extends billResponseSchema with savedBillId)
export const generateBillResponseSchema = billResponseSchema.extend({
  savedBillId: z.string().uuid().nullable(),
});

// Alias for backwards compatibility
export const categoryPricingInputSchema = categoryPricingSchema;

// Custom pricing update schema — one atomic write for a country's WhatsApp
// category pricing and/or its flat RCS price. Symmetric semantics per field:
// OMITTED means "leave unchanged"; PRESENT means "set to this" — where an empty
// `pricing` {} clears the country's WhatsApp overrides and a null `rcsPrice`
// clears its RCS override. At least one of the two must be provided (an update
// with neither would be a silent no-op). RCS has no category.
export const customPricingUpdateSchema = z
  .object({
    isoCode: SupportedCountryCode,
    pricing: categoryPricingSchema.optional(),
    rcsPrice: z.number().positive().nullable().optional(),
  })
  .refine(d => d.pricing !== undefined || d.rcsPrice !== undefined, {
    message: 'Provide pricing and/or rcsPrice',
  });

// Response schema for custom pricing
export const customPricingResponseSchema = z.object({
  businessId: z.number(),
  customPricing: CustomPricingConfig.nullish(),
});

// Legacy aliases for backwards compatibility
export const indiaPricingUpdateSchema = customPricingUpdateSchema;
export const indiaPricingResponseSchema = customPricingResponseSchema;

export type AdminOrg = z.infer<typeof adminOrgSchema>;
export type AdminBusiness = z.infer<typeof adminBusinessSchema>;
export type UpdateBusinessAdminRequest = z.infer<
  typeof updateBusinessAdminRequestSchema
>;
export type UpdateBusinessAdminResponse = z.infer<
  typeof updateBusinessAdminResponseSchema
>;
export type MessagingVolumeRow = z.infer<typeof messagingVolumeRowSchema>;
export type MessagingVolumeByCountryRow = z.infer<
  typeof messagingVolumeByCountryRowSchema
>;
export type MessagingVolumeWithCost = z.infer<
  typeof messagingVolumeWithCostSchema
>;
export type OpenAiUsageRow = z.infer<typeof openAiUsageRowSchema>;
export type BillResponse = z.infer<typeof billResponseSchema>;
export type GenerateBillResponse = z.infer<typeof generateBillResponseSchema>;
export type BillRange = z.infer<typeof billRangeSchema>;
export type CategoryPricingInput = z.infer<typeof categoryPricingInputSchema>;
export type CustomPricingUpdate = z.infer<typeof customPricingUpdateSchema>;
export type CustomPricingResponse = z.infer<typeof customPricingResponseSchema>;
// Legacy aliases
export type IndiaPricingUpdate = CustomPricingUpdate;
export type IndiaPricingResponse = CustomPricingResponse;

// ============================================
// Billing Schemas (for Zoho integration)
// ============================================

// Saved billing record schema
export const savedBillingSchema = z.object({
  id: z.string().uuid(),
  businessId: z.number(),
  phoneNumber: z.string(),
  periodStart: z.string(),
  periodEnd: z.string(),
  billData: billResponseSchema,
  volumeSource: billTypeSchema.default(BillType.STANDARD),
  remarks: z.string().nullable().optional(),
  business: adminBusinessSchema.optional(),
  createdAt: z.union([z.string(), z.date()]),
  updatedAt: z.union([z.string(), z.date()]),
});

// Generate all bills request (alias for billRangeSchema)
export const generateAllBillsRequestSchema = billRangeSchema;

// Generate all bills response item schemas
export const generatedBillItemSchema = z.object({
  businessId: z.number(),
  businessName: z.string(),
  billId: z.string(),
});

export const skippedBillItemSchema = z.object({
  businessId: z.number(),
  businessName: z.string(),
  reason: z.string(),
});

export const billErrorItemSchema = z.object({
  businessId: z.number(),
  businessName: z.string(),
  error: z.string(),
});

// Generate all bills response
export const generateAllBillsResponseSchema = z.object({
  summary: z.object({
    total: z.number(),
    generated: z.number(),
    skipped: z.number(),
    errors: z.number(),
  }),
  generated: z.array(generatedBillItemSchema),
  skipped: z.array(skippedBillItemSchema),
  errors: z.array(billErrorItemSchema),
});

// Update bill request (for editing saved bills)
export const updateBillRequestSchema = z.object({
  billData: billResponseSchema,
});

// Get bills query params
export const getBillsQuerySchema = z.object({
  businessId: z.string().optional(),
  phoneNumber: z.string().optional(),
  periodStart: z.string().optional(),
  periodEnd: z.string().optional(),
  page: z.string().optional(),
  limit: z.string().optional(),
});

// Get bills response
export const getBillsResponseSchema = z.object({
  bills: z.array(savedBillingSchema),
  total: z.number(),
  page: z.number().optional(),
  limit: z.number().optional(),
});

// ============================================
// Zoho Integration Schemas
// ============================================

// Push to Zoho single item schema
export const pushToZohoItemSchema = z.object({
  zohoCustomerId: z.string(),
  zohoCustomerName: z.string(),
  periodStart: z.string(),
  periodEnd: z.string(),
  subscriptionAmount: z.number().nonnegative().optional(),
  addMessagingCharges: z.boolean().optional(), // Whether to add messaging volume line items
  customerType: z.nativeEnum(ZohoCustomerType).optional(), // SMB or Enterprise
});

// Push to Zoho request (array of items)
export const pushToZohoRequestSchema = z.object({
  items: z.array(pushToZohoItemSchema).min(1),
});

// Zoho estimate / bill-push record schema (admin view of a customer_bill_push
// row, used by the estimates list, estimate-by-id and push responses).
export const customerBillPushSchema = z.object({
  id: z.string().uuid(),
  zohoCustomerId: z.string(),
  zohoCustomerName: z.string(),
  periodStart: z.string(),
  periodEnd: z.string(),
  // SMB rows delete the throwaway estimate after reading its total, so these are
  // null there; Enterprise rows keep them populated.
  zohoEstimateId: z.string().nullable().optional(),
  zohoEstimateNumber: z.string().nullable().optional(),
  totalAmount: z.number(),
  currency: z.string(),
  includedBusinessIds: z.array(z.number()),
  createdAt: z.union([z.string(), z.date()]),
  // SMB lifecycle tracking (optional — Enterprise estimate rows omit these).
  // Lets the admin dashboard monitor payment-link generation, payment, and
  // whether the Zoho invoice was created.
  paymentStatus: z.nativeEnum(PaymentStatus).optional(),
  paymentLinkUrl: z.string().nullable().optional(),
  zohoInvoiceId: z.string().nullable().optional(),
  zohoInvoiceNumber: z.string().nullable().optional(),
  paidAt: z.string().nullable().optional(),
  /* Enterprise direct-invoice flow: last known Zoho status of zohoInvoiceId
     ('draft' | 'sent' | 'void' | …), refreshed whenever we touch the invoice.
     Drives the "is this invoice still live" idempotency check, so the admin
     can void in Zoho and re-push without deleting the bill-push row. */
  zohoInvoiceStatus: z.string().nullable().optional(),
  // Mailing audit: when the invoice was last emailed and to whom.
  lastEmailedAt: z.string().nullable().optional(),
  lastEmailedTo: z.array(z.string()).nullable().optional(),
  // Push outcome for this row: created a new bill-push, or reused an existing one.
  action: z.enum(['created', 'reused']).optional(),
});

// Push to Zoho result item (success or error)
export const pushToZohoResultItemSchema = z.object({
  zohoCustomerId: z.string(),
  zohoCustomerName: z.string(),
  success: z.boolean(),
  estimate: customerBillPushSchema.optional(),
  error: z.string().optional(),
});

// Push to Zoho response (array of results)
export const pushToZohoResponseSchema = z.object({
  results: z.array(pushToZohoResultItemSchema),
});

/* Email invoices request. Always an array so the single-row action and the
   bulk action share one endpoint (single = one id). Ids are OUR bill-push row
   ids, not Zoho invoice ids — the server resolves the Zoho invoice itself and
   stamps the mailing audit back onto the row. */
export const emailBillInvoicesRequestSchema = z.object({
  /* Capped per request because each invoice costs ~4 sequential Zoho calls
     (read → email content → send → re-read): a bigger batch would sit near the
     org's Zoho rate limit and risk an ALB idle timeout mid-send. The dashboard
     chunks a larger selection into successive requests. */
  billPushIds: z.array(z.string().uuid()).min(1).max(10),
});

// Per-invoice outcome — one failure never hides the rest of the batch.
export const emailBillInvoiceResultItemSchema = z.object({
  billPushId: z.string().uuid(),
  zohoCustomerName: z.string(),
  success: z.boolean(),
  zohoInvoiceNumber: z.string().nullable().optional(),
  // Recipients Zoho actually sent to (resolved live from the contact persons).
  emailedTo: z.array(z.string()).optional(),
  emailedCc: z.array(z.string()).optional(),
  error: z.string().optional(),
});

export const emailBillInvoicesResponseSchema = z.object({
  results: z.array(emailBillInvoiceResultItemSchema),
});

// Get Zoho estimates query params
export const getZohoEstimatesQuerySchema = z.object({
  zohoCustomerId: z.string().optional(),
  periodStart: z.string().optional(),
  periodEnd: z.string().optional(),
  page: z.string().optional(),
  limit: z.string().optional(),
});

// Get Zoho estimates response
export const getZohoEstimatesResponseSchema = z.object({
  estimates: z.array(customerBillPushSchema),
  total: z.number(),
});

// Zoho customer from API (transformed to camelCase)
export const zohoCustomerSchema = z.object({
  contactId: z.string(),
  contactName: z.string(),
  companyName: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  subscriptionAmount: z.number().optional(), // Monthly platform subscription
  addMessagingCharges: z.boolean().optional(), // Whether to add messaging volume line items
  customerType: z.nativeEnum(ZohoCustomerType).optional(), // SMB or Enterprise
  gstTreatment: z.string().optional(), // 'business_gst' (registered) | 'business_none' | …
  gstNo: z.string().optional(), // 15-char GSTIN; empty/absent when unregistered
});

// Get Zoho customers response
export const getZohoCustomersResponseSchema = z.object({
  customers: z.array(zohoCustomerSchema),
});

/* Admin customer-id mappings (per-row edit and the CSV upload). Each item
   carries either or both ids for a business; an omitted id is left unchanged,
   null or empty string clears it. Both are set here because every customer is
   invoiced in Zoho AND pushed to Route Mobile's Odoo — two ids, one business. */
export const updateZohoMappingRequestSchema = z.object({
  mappings: z.array(
    z
      .object({
        businessId: z.number(),
        zohoCustomerId: z.string().nullable().optional(),
        odooCustomerId: z.string().nullable().optional(),
        booksUsername: z.string().nullable().optional(),
      })
      .refine(
        m =>
          m.zohoCustomerId !== undefined ||
          m.odooCustomerId !== undefined ||
          m.booksUsername !== undefined,
        { message: 'Provide zohoCustomerId, odooCustomerId or booksUsername' },
      ),
  ),
});

// Update mapping response
export const updateZohoMappingResponseSchema = z.object({
  total: z.number(),
  updated: z.number(),
  errors: z.array(
    z.object({
      businessId: z.number(),
      error: z.string(),
    }),
  ),
});

// Save bill params (used by both frontend and backend)
export const saveBillParamsSchema = z.object({
  businessId: z.number(),
  phoneNumber: z.string(),
  periodStart: z.string(), // ISO date string
  periodEnd: z.string(), // ISO date string
  billData: billResponseSchema,
  volumeSource: billTypeSchema.default(BillType.STANDARD),
  remarks: z.string().nullable().optional(),
});

// Billing types
export type SaveBillParams = z.infer<typeof saveBillParamsSchema>;
export type SavedBilling = z.infer<typeof savedBillingSchema>;
export type GenerateAllBillsRequest = z.infer<
  typeof generateAllBillsRequestSchema
>;
export type GenerateAllBillsResponse = z.infer<
  typeof generateAllBillsResponseSchema
>;
export type UpdateBillRequest = z.infer<typeof updateBillRequestSchema>;
export type GetBillsQuery = z.infer<typeof getBillsQuerySchema>;
export type GetBillsResponse = z.infer<typeof getBillsResponseSchema>;

// Zoho types
export type PushToZohoItem = z.infer<typeof pushToZohoItemSchema>;
export type PushToZohoRequest = z.infer<typeof pushToZohoRequestSchema>;
export type PushToZohoResultItem = z.infer<typeof pushToZohoResultItemSchema>;
export type CustomerBillPushRecord = z.infer<typeof customerBillPushSchema>;
export type PushToZohoResponse = z.infer<typeof pushToZohoResponseSchema>;
export type EmailBillInvoicesRequest = z.infer<
  typeof emailBillInvoicesRequestSchema
>;
export type EmailBillInvoiceResultItem = z.infer<
  typeof emailBillInvoiceResultItemSchema
>;
export type EmailBillInvoicesResponse = z.infer<
  typeof emailBillInvoicesResponseSchema
>;
export type GetZohoEstimatesQuery = z.infer<typeof getZohoEstimatesQuerySchema>;
export type GetZohoEstimatesResponse = z.infer<
  typeof getZohoEstimatesResponseSchema
>;
export type ZohoCustomer = z.infer<typeof zohoCustomerSchema>;
export type GetZohoCustomersResponse = z.infer<
  typeof getZohoCustomersResponseSchema
>;
export type UpdateZohoMappingRequest = z.infer<
  typeof updateZohoMappingRequestSchema
>;
export type UpdateZohoMappingResponse = z.infer<
  typeof updateZohoMappingResponseSchema
>;

// ============================================
// Karix CSV Upload Schemas
// ============================================

// Single row from Karix CSV
// Required: date, phoneNumber, country, and conversation counts for billing
// Optional: wabaId, parentOrgname, acode, esmeaddr (non-essential metadata)
export const karixCsvRowSchema = z.object({
  // Required fields for billing
  date: z.string(), // YYYY-MM-DD format
  phoneNumber: z.string(),
  country: z.string(),
  marketingConv: z.number().default(0),
  authenticationConv: z.number().default(0),
  serviceConv: z.number().default(0),
  utilityConv: z.number().default(0),
  // Optional metadata fields — WABA ID must be purely numeric (digits only).
  // Scientific notation like 1.77921E+15 indicates Excel corruption.
  wabaId: z
    .string()
    .regex(/^\d+$/, {
      message:
        'WABA ID must contain only digits. Non-numeric values (e.g. 1.77921E+15) indicate the CSV was corrupted by Excel. Please re-export the CSV without opening it in a spreadsheet application.',
    })
    .optional(),
  parentOrgname: z.string().optional(),
  acode: z.string().optional(),
  esmeaddr: z.string().optional(),
  businessInitiatedRegularConv: z.number().default(0),
  userInitiatedRegularConv: z.number().default(0),
  totalRegularConv: z.number().default(0),
});

// Upload Karix CSV request (array of rows)
export const uploadKarixCsvRequestSchema = z.object({
  rows: z.array(karixCsvRowSchema).min(1, 'At least one row is required'),
});

// Upload Karix CSV response
export const uploadKarixCsvResponseSchema = z.object({
  totalRows: z.number(),
  insertedRows: z.number(),
  updatedRows: z.number(),
});

// Karix types
export type KarixCsvRow = z.infer<typeof karixCsvRowSchema>;
export type UploadKarixCsvRequest = z.infer<typeof uploadKarixCsvRequestSchema>;
export type UploadKarixCsvResponse = z.infer<
  typeof uploadKarixCsvResponseSchema
>;

// ============================================
// Zoho Estimate Param Schemas
// ============================================

export const zohoEstimateIdParamSchema = z.object({
  zohoEstimateId: z.string().min(1, 'zohoEstimateId is required'),
});

// ============================================
// Zoho Estimate Actions Schemas
// ============================================

// Mark estimate as accepted response
export const markEstimateAcceptedResponseSchema = z.object({
  zohoEstimateId: z.string(),
  status: z.string(),
  message: z.string(),
});
export type MarkEstimateAcceptedResponse = z.infer<
  typeof markEstimateAcceptedResponseSchema
>;

// Estimate details response (fetched on-demand from Zoho)
export const estimateDetailsResponseSchema = z.object({
  zohoEstimateId: z.string(),
  estimateNumber: z.string(),
  status: z.string(),
  total: z.number(),
  acceptRetainer: z.boolean(),
  retainerPercentage: z.number(),
  retainerInvoiceId: z.string().optional(),
  retainerInvoiceNumber: z.string().optional(),
  retainerInvoiceUrl: z.string().optional(),
  estimateUrl: z.string().optional(),
});
export type EstimateDetailsResponse = z.infer<
  typeof estimateDetailsResponseSchema
>;

// ============================================
// Zoho Retainer Invoice Activation Schemas
// ============================================

// Individual activate: response after adding payment method + marking sent
export const activateRetainerInvoiceResponseSchema = z.object({
  zohoEstimateId: z.string(),
  retainerInvoiceId: z.string(),
  message: z.string(),
});
export type ActivateRetainerInvoiceResponse = z.infer<
  typeof activateRetainerInvoiceResponseSchema
>;

// Bulk activate request (reuses billRangeSchema: startDate + endDate)
export const bulkActivateRetainerInvoicesRequestSchema = billRangeSchema;
export type BulkActivateRetainerInvoicesRequest = z.infer<
  typeof bulkActivateRetainerInvoicesRequestSchema
>;

// Single result item for bulk activate
export const bulkActivateResultItemSchema = z.object({
  zohoEstimateId: z.string(),
  zohoCustomerName: z.string(),
  success: z.boolean(),
  retainerInvoiceId: z.string().optional(),
  error: z.string().optional(),
});
export type BulkActivateResultItem = z.infer<
  typeof bulkActivateResultItemSchema
>;

// Bulk activate response
export const bulkActivateRetainerInvoicesResponseSchema = z.object({
  results: z.array(bulkActivateResultItemSchema),
});
export type BulkActivateRetainerInvoicesResponse = z.infer<
  typeof bulkActivateRetainerInvoicesResponseSchema
>;

// ============================================
// Zoho Contact Update Schemas
// ============================================

// Update Zoho contact custom fields request
export const updateZohoContactRequestSchema = z.object({
  subscriptionAmount: z.number().nonnegative().optional(),
  addMessagingCharges: z.boolean().optional(),
  customerType: z.nativeEnum(ZohoCustomerType).optional(),
});
export type UpdateZohoContactRequest = z.infer<
  typeof updateZohoContactRequestSchema
>;

// Update Zoho contact response
export const updateZohoContactResponseSchema = z.object({
  contactId: z.string(),
  contactName: z.string(),
  message: z.string(),
});
export type UpdateZohoContactResponse = z.infer<
  typeof updateZohoContactResponseSchema
>;

// ============================================
// Customer-Facing Billing Schemas
// ============================================

// Org estimates query (reuses billRangeSchema: startDate + endDate)
export const getOrgEstimatesQuerySchema = billRangeSchema;
export type GetOrgEstimatesQuery = z.infer<typeof getOrgEstimatesQuerySchema>;

// Org estimates response (reuses customerBillPushSchema)
export const getOrgEstimatesResponseSchema = z.object({
  estimates: z.array(customerBillPushSchema),
  total: z.number(),
});
export type GetOrgEstimatesResponse = z.infer<
  typeof getOrgEstimatesResponseSchema
>;

// ---- SMB bills (customer-facing) ----

// Canonical Zoho line item — the SINGLE type used both for Zoho API payloads
// (backend) and the customer-facing bill breakup. Defined here in shared so FE
// and backend share one type; the backend re-exports it from types/zohoTypes so
// existing imports keep working. hsn_or_sac appears on the customer's invoice so
// it's safe to surface; tds_tax_id is internal and simply isn't populated for SMB
// bills. The client computes amount = rate × quantity for display.
export const zohoLineItemSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  rate: z.number(),
  quantity: z.number(),
  unit: z.string().optional(),
  hsn_or_sac: z.string().optional(),
  tds_tax_id: z.string().optional(),
});
export type ZohoLineItem = z.infer<typeof zohoLineItemSchema>;

// A single SMB bill as shown to the customer. The draft estimate/quote is
// intentionally NOT exposed (throwaway draft) — only the Razorpay payment link.
// paymentLinkUrl is set only while PENDING (retryable); invoiceNumber/paidAt only
// once PAID. totalAmount is GST-inclusive (the charged amount); line items are
// pre-GST, so the client shows subtotal = Σ (rate × quantity), GST = total − subtotal.
export const zohoCustomerBillSchema = z.object({
  id: z.string().uuid(),
  periodStart: z.string(),
  periodEnd: z.string(),
  totalAmount: z.number(),
  currency: z.string(),
  paymentStatus: z.nativeEnum(PaymentStatus),
  paymentLinkUrl: z.string().nullable(),
  invoiceNumber: z.string().nullable(),
  paidAt: z.string().nullable(),
  lineItems: z.array(zohoLineItemSchema),
  createdAt: z.string(),
});
export type ZohoCustomerBill = z.infer<typeof zohoCustomerBillSchema>;

// GET /business/billing/zoho-customer-bills query — optional date range
// (omit ⇒ all bills).
export const zohoCustomerGetBillsQuerySchema = z.object({
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});
export type ZohoCustomerGetBillsQuery = z.infer<
  typeof zohoCustomerGetBillsQuerySchema
>;

// GET /business/billing/zoho-customer-bills response — full SMB history
// (excludes NONE rows).
export const zohoCustomerGetBillsResponseSchema = z.object({
  bills: z.array(zohoCustomerBillSchema),
  total: z.number(),
});
export type ZohoCustomerGetBillsResponse = z.infer<
  typeof zohoCustomerGetBillsResponseSchema
>;

// GET /business/billing/zoho-customer-bills/unpaid-summary — lightweight payload
// for the app-wide banner. totalDue = Σ of unpaid (PENDING + FAILED) amounts.
export const zohoCustomerUnpaidBillsSummaryResponseSchema = z.object({
  hasUnpaid: z.boolean(),
  count: z.number(),
  totalDue: z.number(),
  currency: z.string(),
});
export type ZohoCustomerUnpaidBillsSummaryResponse = z.infer<
  typeof zohoCustomerUnpaidBillsSummaryResponseSchema
>;

// GET /business/billing/zoho-customer-bills/:id/invoice-url — on-demand fetch of
// the Zoho invoice_url for a PAID bill (null if Zoho hasn't surfaced one).
export const zohoCustomerBillIdParamSchema = z.object({
  id: z.string().uuid(),
});
export type ZohoCustomerBillIdParam = z.infer<
  typeof zohoCustomerBillIdParamSchema
>;

export const zohoCustomerBillInvoiceUrlResponseSchema = z.object({
  invoiceUrl: z.string().nullable(),
});
export type ZohoCustomerBillInvoiceUrlResponse = z.infer<
  typeof zohoCustomerBillInvoiceUrlResponseSchema
>;

// ============================================
// Customer GSTIN self-service (top of bills)
// ============================================

// Structural GSTIN check ONLY (no checksum — the customer attests to their own
// GSTIN). 15 chars: 2-digit state code + 10-char PAN block + entity char + 'Z' +
// final char. The state code (first 2 digits) is additionally validated against
// the known GST-state map on the server, since it drives place_of_contact.
export const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/;

// PUT /business/billing/gstin — set or clear the customer's GSTIN. A valid GSTIN
// registers the contact; null deregisters it (one endpoint, since both just set
// the value of a single field).
export const updateGstinRequestSchema = z
  .object({
    gstin: z
      .string()
      .trim()
      .transform(v => v.toUpperCase())
      .refine(v => GSTIN_REGEX.test(v), 'Enter a valid 15-character GSTIN')
      .nullable(),
  })
  .strict();
export type UpdateGstinRequest = z.infer<typeof updateGstinRequestSchema>;

// Current registration status of the customer's Zoho contact. gstin/placeOfContact
// are null when unregistered. Returned as-is by PUT (always a concrete status).
export const gstinStatusResponseSchema = z.object({
  isRegistered: z.boolean(),
  gstin: z.string().nullable(),
  placeOfContact: z.string().nullable(),
});
export type GstinStatusResponse = z.infer<typeof gstinStatusResponseSchema>;

// GET response is nullable: null means the GSTIN feature doesn't apply to this
// customer (partner/white-label org, or non-SMB) and the client hides the control.
export const gstinStatusGetResponseSchema =
  gstinStatusResponseSchema.nullable();
export type GstinStatusGetResponse = z.infer<
  typeof gstinStatusGetResponseSchema
>;

// ============================================
// Odoo customer id — customer self-service
// ============================================

/* Every customer is invoiced in Zoho (our books) AND pushed to Route Mobile's
   Odoo, so a business carries both ids. The Odoo one is entered here by the
   customer's onboarding contact; grouping is by value — businesses sharing an
   id land on one Odoo invoice. */

// GET /business/billing/odoo-mapping
export const odooMappingResponseSchema = z.object({
  odooCustomerId: z.string().nullable(),
  booksUsername: z.string().nullable(),
});
export type OdooMappingResponse = z.infer<typeof odooMappingResponseSchema>;

/* PUT /business/billing/odoo-mapping — a string sets a field, null clears it.
   booksUsername (the per-business Odoo sub-account) is optional so bundles
   shipped before the field existed keep saving the id alone. */
export const updateOdooMappingRequestSchema = z
  .object({
    odooCustomerId: z.string().trim().max(128).nullable(),
    booksUsername: z.string().trim().max(128).nullable().optional(),
  })
  .strict();
export type UpdateOdooMappingRequest = z.infer<
  typeof updateOdooMappingRequestSchema
>;

// ============================================
// Employee Management Schemas
// ============================================

export const adminEmployeeSchema = z.object({
  id: z.number(),
  name: z.string(),
  email: z.string(),
  isActive: z.boolean(),
  canViewCost: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  business: z
    .object({
      id: z.number(),
      name: z.string(),
    })
    .nullable()
    .optional(),
  org: z
    .object({
      id: z.number(),
      name: z.string(),
    })
    .nullable()
    .optional(),
  rolePermission: z
    .object({
      id: z.number(),
      role: z.string(),
    })
    .nullable()
    .optional(),
});

export type AdminEmployee = z.infer<typeof adminEmployeeSchema>;

export const adminEmployeesResponseSchema = z.array(adminEmployeeSchema);

export const updateEmployeeRequestSchema = z.object({
  canViewCost: z.boolean().optional(),
});

export type UpdateEmployeeRequest = z.infer<typeof updateEmployeeRequestSchema>;

export const updateEmployeeResponseSchema = z.object({
  id: z.number(),
  canViewCost: z.boolean().optional(),
});

export type UpdateEmployeeResponse = z.infer<
  typeof updateEmployeeResponseSchema
>;
